// frontend/src/contexts/AuthContext.tsx
// ✅ FIXED: Added missing login method for BookingAuthStep component

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '@/lib/api/authService';
import { tokenManager } from '@/lib/httpClient';

// Re-export types from authService to maintain compatibility
export type {
  User,
  EmailVerificationStatus,
  LoginCredentials,
  RegisterData,
  AuthResponse,
  PasswordResetData,
  PasswordResetConfirmData,
  ChangePasswordData
} from '@/lib/api/authService';

// Auth result for context methods
export interface AuthResult {
  success: boolean;
  message: string;
  errors?: Record<string, string[]>;
  user?: any;
  requiresVerification?: boolean;
  requiresApproval?: boolean;
  next_step?: string;
  email_verification?: any;
}

// Context interface - simplified to focus on state management
export interface AuthContextValue {
  user: any | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  emailVerificationStatus: any | null;
  initializeAuth: () => Promise<void>;
  clearAuth: () => void;
  setUserData: (userData: any) => void;
  setEmailVerificationData: (data: any) => void;
  hasRole: (role: string) => boolean;
  logout: () => Promise<void>;
  // ✅ ADDED: Missing login method for backward compatibility
  login: (token: string, user: any) => Promise<void>;
}

// Create context
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [emailVerificationStatus, setEmailVerificationStatus] = useState<any | null>(null);

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  // Computed values
  const isAuthenticated = !!user;

  // Initialize authentication - only checks token and sets user
  const initializeAuth = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Check if we have a token
      const token = tokenManager.getToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Get current user
      const response = await authService.getCurrentUser();
      
      if (response.success && response.data) {
        setUser(response.data.user);
        setEmailVerificationStatus(null);
      } else {
        // Token might be invalid, clear it
        tokenManager.clearTokens();
        setUser(null);
        setEmailVerificationStatus(null);
      }
    } catch (error: any) {
      console.error('Auth initialization error:', error);
      
      // Only clear tokens for auth-related errors (401, 403), not server errors (500)
      if (error?.response?.status === 401) {
        console.log('Auth check failed with 401, attempting token refresh...');
        // Try to refresh token before clearing auth
        try {
          const refreshSuccess = await tokenManager.refreshAccessToken();
          if (refreshSuccess) {
            console.log('Token refresh successful, retrying auth check...');
            // Retry getting current user with new token
            const retryResponse = await authService.getCurrentUser();
            if (retryResponse.success && retryResponse.data) {
              setUser(retryResponse.data.user);
              setEmailVerificationStatus(null);
              setIsLoading(false);
              return;
            }
          }
        } catch (refreshError) {
          console.log('Token refresh during auth init failed:', refreshError);
        }
        
        // If refresh failed, clear auth state
        tokenManager.clearTokens();
        setUser(null);
        setEmailVerificationStatus(null);
      } else if (error?.response?.status === 403) {
        tokenManager.clearTokens();
        setUser(null);
        setEmailVerificationStatus(null);
      } else if (error?.response?.status >= 500) {
        // Server error - keep user logged in but show they're offline
        console.log('Server error during auth check, keeping user logged in');
      } else {
        // Other errors - clear auth state
        tokenManager.clearTokens();
        setUser(null);
        setEmailVerificationStatus(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Clear authentication state
  const clearAuth = (): void => {
    tokenManager.clearTokens();
    setUser(null);
    setEmailVerificationStatus(null);
  };

  // Set user data (called by hooks after successful operations)
  const setUserData = (userData: any): void => {
    setUser(userData);
  };

  // Set email verification data
  const setEmailVerificationData = (data: any): void => {
    setEmailVerificationStatus(data);
  };

  // hasRole function
  const hasRole = (role: string): boolean => {
    if (!user) return false;
    
    // Handle role checking logic
    const userRole = user.role || '';
    
    // Direct role match
    if (userRole === role) return true;
    
    // Super admin has access to everything
    if (userRole === 'super_admin') return true;
    
    // Admin can access admin and provider routes
    if (userRole === 'admin' && (role === 'admin' || role === 'provider')) return true;
    
    // Provider can access provider routes
    if (userRole === 'provider' && role === 'provider') return true;
    
    // User/Customer can access user routes
    if ((userRole === 'user' || userRole === 'customer') && (role === 'user' || role === 'customer')) return true;
    
    return false;
  };

  // logout function
  const logout = async (): Promise<void> => {
    try {
      // Try to logout on server
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Even if server logout fails, clear local data
    } finally {
      // Always clear local auth state
      clearAuth();
    }
  };

  // ✅ ADDED: login method for backward compatibility (used by BookingAuthStep)
  const login = async (token: string, userData: any): Promise<void> => {
    try {
      // Store the token
      tokenManager.setTokens(token, undefined); // No refresh token provided in this legacy method
      
      // Set user data
      setUser(userData);
      
      // Clear any previous verification status
      setEmailVerificationStatus(null);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Context value
  const contextValue: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated,
    emailVerificationStatus,
    initializeAuth,
    clearAuth,
    setUserData,
    setEmailVerificationData,
    hasRole,
    logout,
    login // ✅ ADDED: Include login method
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;